import Enterprise24 from '@carbon/icons-react/es/enterprise/24';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { TimesheetProjectReport } from 'api/timesheets/reports/types';
import { Avatar } from 'components/atoms/avatar';
import {
  NameCell,
  NameCellWithTooltip,
} from 'components/organisms/timesheets/common/cells/name-cell';
import { PercentWithTimeCell } from 'components/organisms/timesheets/common/cells/percent-with-time-cell';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { calculatePercentage } from 'components/organisms/timesheets/common/utils';
import {
  billableMinutesSorter,
  nonBillableMinutesSorter,
  projectNameSorter,
  totalMinutesSorter,
} from './sorter';

export const useCategoryWiseProjectConfig = ({
  noOfRows,
}: {
  noOfRows: number;
}) => {
  return [
    {
      title: (
        <FormattedMessage
          id="timesheets.column.projects"
          values={{ count: noOfRows }}
        />
      ),
      key: 'projects',
      width: '310px',
      render: (_, record: TimesheetProjectReport) => {
        const { project } = record;

        if (project) {
          return (
            <NameCellWithTooltip
              logo={
                <Avatar
                  name={project.projectName}
                  src={project.customer.companyLogoUrl}
                />
              }
              primaryText={project.projectName}
              secondaryText={project.customer.companyName}
            />
          );
        }

        return (
          <NameCell
            logo={<Enterprise24 />}
            primaryText={
              <FormattedMessage id="timesheets.labels.non_project" />
            }
          />
        );
      },
      summaryRender: () => <FormattedMessage id="timesheets.column.total" />,
      defaultSortOrder: 'ascend',
      sorter: projectNameSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.hours_tracked" />,
      key: 'hoursTracked',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        return (
          <BaseCell>
            <FormatTime value={billableMinutes + nonBillableMinutes} />
          </BaseCell>
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => (
        <FormatTime
          value={
            summaryRecord.billableMinutes + summaryRecord.nonBillableMinutes
          }
        />
      ),
      sorter: totalMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.billable_hours" />,
      key: 'billableHours',
      width: '200px',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          billableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={billableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => (
        <FormatTime value={summaryRecord.billableMinutes} />
      ),
      sorter: billableMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.non_billable_hours" />,
      key: 'nonBillableHours',
      render: (_, record: TimesheetProjectReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          nonBillableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={nonBillableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetProjectReport) => (
        <FormatTime value={summaryRecord.nonBillableMinutes} />
      ),
      sorter: nonBillableMinutesSorter,
      showSorterTooltip: false,
    },
  ];
};
